'use client'

import * as React from 'react'
import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import { cn } from '@/components/new/lib/utils'
import { usePopupLayerContainer } from '@/components/new/ui/popup-layer'

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T) => {
    if (typeof ref === 'function') {
        ref(value)
        return
    }

    if (ref) {
        ;(ref as React.MutableRefObject<T>).current = value
    }
}

function Popover({
    onOpenChange,
    open: openProp,
    defaultOpen = false,
    actionsRef,
    ...props
}: PopoverPrimitive.Root.Props) {
    const internalActionsRef = React.useRef<PopoverPrimitive.Root.Actions | null>(null)
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)

    const open = openProp ?? uncontrolledOpen

    const handleOpenChange = React.useCallback<NonNullable<PopoverPrimitive.Root.Props['onOpenChange']>>(
        (nextOpen, eventDetails) => {
            setUncontrolledOpen(nextOpen)
            onOpenChange?.(nextOpen, eventDetails)
        },
        [onOpenChange]
    )

    React.useEffect(() => {
        assignRef(actionsRef, internalActionsRef.current)
    }, [actionsRef, open])

    React.useEffect(() => {
        if (!open || typeof window === 'undefined') {
            return
        }

        const closeWhenIframeGetsFocus = () => {
            window.setTimeout(() => {
                const activeElement = document.activeElement

                if (!(activeElement instanceof HTMLIFrameElement)) {
                    return
                }

                if (activeElement.closest('[data-slot="popover-content"], [data-slot="popover-trigger"]')) {
                    return
                }

                internalActionsRef.current?.close()
            }, 0)
        }

        window.addEventListener('blur', closeWhenIframeGetsFocus)
        document.addEventListener('focusin', closeWhenIframeGetsFocus, true)

        return () => {
            window.removeEventListener('blur', closeWhenIframeGetsFocus)
            document.removeEventListener('focusin', closeWhenIframeGetsFocus, true)
        }
    }, [open])

    return (
        <PopoverPrimitive.Root
            data-slot="popover"
            actionsRef={internalActionsRef}
            defaultOpen={defaultOpen}
            open={openProp}
            onOpenChange={handleOpenChange}
            {...props}
        />
    )
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
    return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
    className,
    align = 'center',
    alignOffset = 0,
    side = 'bottom',
    sideOffset = 4,
    ...props
}: PopoverPrimitive.Popup.Props &
    Pick<PopoverPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>) {
    const popupLayerContainer = usePopupLayerContainer()

    return (
        <PopoverPrimitive.Portal container={popupLayerContainer}>
            <PopoverPrimitive.Positioner
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
                className="isolate z-[60]"
            >
                <PopoverPrimitive.Popup
                    data-slot="popover-content"
                    className={cn(
                        'origin-(--transform-origin) bg-popover text-popover-foreground ring-foreground/10 outline-hidden data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex w-72 flex-col gap-4 rounded-md p-4 text-sm shadow-md ring-1 duration-100',
                        className
                    )}
                    {...props}
                />
            </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
    )
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="popover-header" className={cn('flex flex-col gap-1 text-sm', className)} {...props} />
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
    return <PopoverPrimitive.Title data-slot="popover-title" className={cn('font-medium', className)} {...props} />
}

function PopoverDescription({ className, ...props }: PopoverPrimitive.Description.Props) {
    return (
        <PopoverPrimitive.Description
            data-slot="popover-description"
            className={cn('text-muted-foreground', className)}
            {...props}
        />
    )
}

export { Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger }
